// src/annihilation/enemies/enemyTaxonomy.js
// ============================================================
//  ANNIHILATION — THE enemy taxonomy (Batch 6).
//  Source of truth: ANNIHILATION_SYSTEMS_SPEC (v2) Part 5,
//  ANNIHILATION_WORLD_ATLAS (Part 3 rosters).
//
//  ONE taxonomy file, reused by every zone from here to launch.
//  This is the batch that sets the pattern — every later zone
//  batch ADDS ENTRIES HERE (a def is one object), it never
//  builds a per-zone enemy list. (One source of truth law.)
//
//  An enemy is fully specified by (spec, "How to Read a Stat
//  Block"): family + role + AI archetype + zone level +
//  signature abilities + drop reference. buildEnemy() turns
//  that line into a live spawn.
//
//  SCALING LAW (spec Part 5, verbatim formulas):
//    anima  = anima_base  × zone_level × anima_multiplier
//    attack = attack_base + zone_level × attack_scalar
//    lyf    = lyf_base    + zone_level × lyf_scalar
//  The spec's table gives the bases; the multiplier/scalar
//  columns aren't printed, but BOTH of the spec's own worked
//  examples (Mook@10 ≈ 200/13/11 · World Boss@70 ≈ 5.6M) solve
//  to multiplier = scalar = 1. Implemented as 1 across all
//  roles, per-role overridable. ⚑ Flagged in the roadmap as a
//  dial, not silently invented.
//
//  XP LAW: stats.js XP_YIELD (Systems Spec Part 4) is the XP
//  law. The role table's own "XP Multiplier" column agrees with
//  Part 4 for Elite/Sub-boss/World Boss when read as ×zone
//  yields, and applies as a multiplier on the STANDARD kill
//  yield for the field roles. ⚑ The two tables disagree if
//  read any other way — flagged in the roadmap (§4 pattern),
//  implemented as: field roles = enemyKill × roleXpMult;
//  Elite / Sub-boss / World Boss = their dedicated Part 4 rows.
// ============================================================

import { XP_YIELD } from "./stats";

// ------------------------------------------------------------
// THE EIGHT ROLES — behavior class, scaling curve, threat.
// Spec table, verbatim bases. mult/scalar = 1 (see header ⚑).
// ------------------------------------------------------------
export const ROLES = {
  mook: {
    id: "mook",
    name: "Mook",
    behavior: "Basic fodder. Approaches and Lyt attacks.",
    threat: "Low",
    animaBase: 20,
    attackBase: 3,
    lyfBase: 1,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: 1,
  },
  skirmisher: {
    id: "skirmisher",
    name: "Skirmisher",
    behavior: "Fast, evasive, hit-and-run. Dodges player abilities.",
    threat: "Low-Mid",
    animaBase: 15,
    attackBase: 5,
    lyfBase: 0,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: 1.5,
  },
  brute: {
    id: "brute",
    name: "Brute",
    behavior: "Slow, high Anima, high Lyf. Big telegraphed swings.",
    threat: "Mid",
    animaBase: 80,
    attackBase: 8,
    lyfBase: 5,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: 3,
  },
  caster: {
    id: "caster",
    name: "Caster",
    behavior: "Ranged ability attacks. Weak in melee.",
    threat: "Mid",
    animaBase: 25,
    attackBase: 10,
    lyfBase: 0,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: 2,
  },
  summoner: {
    id: "summoner",
    name: "Summoner",
    behavior: "Spawns Mooks over time. Must be prioritized.",
    threat: "Mid-High",
    animaBase: 40,
    attackBase: 4,
    lyfBase: 2,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: 2.5,
  },
  elite: {
    id: "elite",
    name: "Elite",
    behavior: "Named rare enemy with a unique mechanic.",
    threat: "High",
    animaBase: 500,
    attackBase: 25,
    lyfBase: 15,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: null, // dedicated Part 4 row: eliteKill
  },
  sub_boss: {
    id: "sub_boss",
    name: "Sub-boss",
    behavior: "Dungeon anchor boss. Multi-phase, 2–4 min fight.",
    threat: "Very High",
    animaBase: 5000,
    attackBase: 60,
    lyfBase: 40,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: null, // dedicated Part 4 row: dungeonBoss
  },
  world_boss: {
    id: "world_boss",
    name: "World Boss",
    behavior: "Raid-tier. Multi-phase, 10+ min, 4–40 players.",
    threat: "Top",
    animaBase: 80000,
    attackBase: 120,
    lyfBase: 80,
    animaMult: 1,
    attackScalar: 1,
    lyfScalar: 1,
    xpMult: null, // dedicated Part 4 row: worldBoss
  },
};

// ------------------------------------------------------------
// THE EIGHT FAMILIES — flavor on top of role. Determines visual
// identity, lineage connection, and the signature-ability pool.
// ------------------------------------------------------------
export const FAMILIES = {
  beast: {
    id: "beast",
    name: "Beast",
    lineage: "Beast-ilk beings",
    visual: "Fur, fangs, claws, feral movement",
    signatureAbilities: ["pounce", "bleed", "pack_tactics"],
  },
  dragon: {
    id: "dragon",
    name: "Dragon",
    lineage: "Dragon-ilk beings",
    visual: "Scaled, winged, elemental",
    signatureAbilities: ["breath_attack", "wing_buffet", "terrain_destruction"],
  },
  machine: {
    id: "machine",
    name: "Machine",
    lineage: "Machine/Time beings",
    visual: "Clockwork, brass, steam, mechanical",
    signatureAbilities: ["self_repair", "swarm_behavior", "clock_tick"],
  },
  humanoid: {
    id: "humanoid",
    name: "Humanoid",
    lineage: "Xulan-ilk, H-series",
    visual: "Bipedal, armored, disciplined",
    signatureAbilities: ["coordinated_attack", "shield", "ritual"],
  },
  construct: {
    id: "construct",
    name: "Construct",
    lineage: "Non-being: statues, guardians, stone wards",
    visual: "Stone, carved, inert-looking until triggered",
    signatureAbilities: ["high_lyf", "aoe_slam"],
  },
  spectral: {
    id: "spectral",
    name: "Spectral",
    lineage: "Ghost variants of any ilk",
    visual: "Translucent, partially phased",
    signatureAbilities: ["phase_dodge"],
  },
  corrupted: {
    id: "corrupted",
    name: "Corrupted",
    lineage: "Fog-touched variants of any family",
    visual: "Decayed, twisted version of a base family",
    signatureAbilities: ["nihil_application", "fog_empowered"],
  },
  nihil_born: {
    id: "nihil_born",
    name: "Nihil-born",
    lineage: "No lineage — J'ynxi's direct creations",
    visual: "Wrong anatomy, unsettling proportions",
    signatureAbilities: ["reality_warp"],
  },
};

// ------------------------------------------------------------
// THE FIVE AI ARCHETYPES — the enemy's 'personality'. Zones run
// these as state machines; the ids here are the one vocabulary.
// ------------------------------------------------------------
export const AI = {
  aggressor: {
    id: "aggressor",
    name: "Aggressor",
    behavior: "Closes distance, attacks the nearest player, ignores positioning.",
  },
  skittish: {
    id: "skittish",
    name: "Skittish",
    behavior: "Maintains distance, dodges abilities, retreats when low.",
  },
  keeper: {
    id: "keeper",
    name: "Keeper",
    behavior: "Defends a spawn point or summon area. Pulls toward home.",
  },
  predator: {
    id: "predator",
    name: "Predator",
    behavior: "Targets the weakest or most isolated player. Splits the team.",
  },
  choreographer: {
    id: "choreographer",
    name: "Choreographer",
    behavior: "Runs a scripted sequence of attacks with telegraphed phases.",
  },
};

// ------------------------------------------------------------
// SCALING — the spec formulas, one implementation.
// ------------------------------------------------------------
export const scaleRole = (roleId, zoneLevel) => {
  const role = ROLES[roleId];
  if (!role) return null;
  return {
    anima: Math.round(role.animaBase * zoneLevel * role.animaMult),
    attack: Math.round(role.attackBase + zoneLevel * role.attackScalar),
    lyf: Math.round(role.lyfBase + zoneLevel * role.lyfScalar),
  };
};

/**
 * XP for a kill — the one place the role table meets the
 * Part 4 XP law (see header ⚑ for the reconciliation).
 */
export const xpForKill = (roleId, zoneLevel) => {
  const role = ROLES[roleId];
  if (!role) return 0;
  if (roleId === "elite") return XP_YIELD.eliteKill * zoneLevel;
  if (roleId === "sub_boss") return XP_YIELD.dungeonBoss * zoneLevel;
  if (roleId === "world_boss") return XP_YIELD.worldBoss * zoneLevel;
  return Math.round(XP_YIELD.enemyKill * zoneLevel * (role.xpMult || 1));
};

// ------------------------------------------------------------
// BLOOD CREST DROPS — per-role dials (caP'n tuning surface, not
// law; the write itself always routes through deltaBloodMoney,
// whose internal id stays `bloodMoney` per the Dial Law — only
// the on-screen name is "Blood Crests").
//
// ⚑ Jul 21 balance pass (caP'n): anchored at Mook = 1 Blood Crest
// per caP'n's ruling, gentle curve scaled from there. This is
// LOWER income per kill than the old set (mook was 2) — but crests
// now have a real sink (the Trainer's Well converts them to Renown,
// stats.CREST_TO_XP_RATE) and leveling is 5× cheaper, so the loop
// flows faster overall despite the smaller drops. Tune freely.
//
// The export id stays BLOOD_MONEY_DROP so no import site breaks
// (Vyren pattern: id fixed, meaning renamed in the docs/UI).
// ------------------------------------------------------------
export const BLOOD_MONEY_DROP = {
  mook: 1, //        the anchor (caP'n: "1 Blood Crest for the weak enemy")
  skirmisher: 2,
  caster: 3,
  summoner: 3,
  brute: 4,
  elite: 30,
  sub_boss: 100, // per clear; the FIRST clear bonus lives on the boss def
  world_boss: 500,
};

// ------------------------------------------------------------
// THE FACTORY — one def + one zone level → one live spawn.
// Defs may override any scaled stat (named bosses carry their
// own explicit numbers — the Matriarch's 8000 Anima is spec).
// The returned shape SATISFIES PlayerController's target
// contract: { id, pos, anima, lyf, bleedStacks?, alive } — plus
// the AI bookkeeping every zone's state machine reads.
// ------------------------------------------------------------
let spawnSeq = 0;

export function buildEnemy(def, zoneLevel, pos) {
  const scaled = scaleRole(def.role, zoneLevel) || { anima: 1, attack: 1, lyf: 0 };
  const anima = def.anima ?? scaled.anima;
  return {
    // target contract (PlayerController)
    id: `${def.id}_${++spawnSeq}`,
    pos: { ...pos },
    anima,
    lyf: def.lyf ?? scaled.lyf,
    bleedStacks: 0,
    alive: true,
    // stat block
    defId: def.id,
    name: def.name,
    family: def.family,
    role: def.role,
    ai: def.ai,
    zoneLevel,
    attack: def.attack ?? scaled.attack,
    animaMax: anima,
    abilities: def.abilities || [],
    // AI bookkeeping (zones own this state; ids are shared vocab)
    state: "idle", // idle | aggro | telegraph | recover | phased
    stateUntil: 0,
    spawnPos: { ...pos },
    telegraphing: false,
  };
}

// ============================================================
//  THE OUTSKIRTS ROSTER — World Atlas Part 3.
//  Every def is one line of the spec's stat-block grammar.
//  Later zones append their own defs below (one taxonomy file,
//  forever — the Batch 6 pattern).
// ============================================================

export const ENEMY_DEFS = {
  // ---- Outskirts North — The Whispering Grove ---------------
  ash_wolf: {
    id: "ash_wolf",
    name: "Ash Wolf",
    family: "beast",
    role: "mook",
    ai: "aggressor",
    abilities: ["pounce"],
    flavor: "Grey-pelted and patient. They hunt the birch shadows in threes.",
  },
  grove_fox: {
    id: "grove_fox",
    name: "Grove Fox",
    family: "beast",
    role: "skirmisher",
    ai: "skittish",
    abilities: ["pounce"],
    dodgeChance: 0.25, // skirmisher law: dodges player abilities
    flavor: "Silver-quick. It watches you decide, then decides faster.",
  },
  corrupted_elk: {
    id: "corrupted_elk",
    name: "Corrupted Elk",
    family: "corrupted",
    role: "brute",
    ai: "aggressor",
    abilities: ["bleed", "nihil_application"],
    appliesBleed: true, // beast-line gore — bleeds the player
    flavor: "Fold-folk fed these once. Something else feeds them now.",
  },
  the_antlered_one: {
    id: "the_antlered_one",
    name: "The Antlered One",
    family: "corrupted",
    role: "elite",
    ai: "predator",
    abilities: ["pounce", "bleed", "nihil_application"],
    appliesBleed: true,
    unique: true, // one alive at a time; a patrol, not a spawn pool
    flavor:
      "A rare named corrupted elk who patrols the zone. Low Aenarchists flee it. That is correct.",
  },

  // ---- The Sunken Fold (dungeon trash) ----------------------
  phantom_prowler: {
    id: "phantom_prowler",
    name: "Phantom Prowler",
    family: "spectral",
    role: "mook",
    ai: "aggressor",
    abilities: ["phase_dodge"],
    phases: true, // spectral law: untouchable while phased
    flavor: "A tiger that forgot it died. It remembers how to hunt.",
  },
  phantom_cub: {
    id: "phantom_cub",
    name: "Phantom Cub",
    family: "spectral",
    role: "mook",
    ai: "keeper", // walks home — home is HER
    // Mechanic piece, not a roster enemy: explicit dials so the
    // peel is possible (role-scaled lyf would make cubs tanks).
    anima: 80,
    lyf: 0,
    attack: 0,
    abilities: [],
    flavor: "It is not attacking. It is going to its mother. That is worse.",
  },

  // ---- Outskirts East — The Glass Flats (Batch 16 wires the
  //      zone; the defs land now so the taxonomy stays whole) --
  scavenger_hound: {
    id: "scavenger_hound",
    name: "Scavenger Hound",
    family: "beast",
    role: "mook",
    ai: "aggressor",
    abilities: ["pack_tactics"],
    flavor: "It eats what the Flats leave. The Flats leave everything.",
  },
  rust_crawler: {
    id: "rust_crawler",
    name: "Rust Crawler",
    family: "machine",
    role: "mook",
    ai: "aggressor",
    abilities: ["swarm_behavior"],
    flavor: "A hand-sized brass thing that still follows its last order.",
  },
  carrion_harrier: {
    id: "carrion_harrier",
    name: "Carrion Harrier",
    family: "beast",
    role: "skirmisher",
    ai: "skittish",
    ranged: true,
    dodgeChance: 0.25,
    abilities: [],
    flavor: "It circles. When it stops circling, move.",
  },
  slag_golem: {
    id: "slag_golem",
    name: "Slag Golem",
    family: "machine",
    role: "brute",
    ai: "aggressor",
    abilities: ["aoe_slam"],
    flavor: "The kiln died. The pour kept walking.",
  },
  the_cartographer: {
    id: "the_cartographer",
    name: "The Cartographer",
    family: "machine",
    role: "elite",
    ai: "predator",
    unique: true,
    abilities: ["clock_tick"],
    flavor: "A roaming, broken-down Z4Z-Demon variant. It is still mapping. You are terrain.",
  },
};

export const getEnemyDef = (defId) => ENEMY_DEFS[defId] || null;

// ============================================================
//  THE BOSS BOOK — nine-slot templates (Systems Spec Part 6).
//  Every boss, dungeon or world, is a filled template. The
//  dungeon file consumes its entry; it never re-authors law.
// ============================================================

export const BOSSES = {
  phantom_matriarch: {
    // 1 · Identity ------------------------------------------------
    id: "phantom_matriarch",
    name: "The Phantom Matriarch",
    lordName: "Sael'esh", // IE_LORD_NAMES_CANON — locked
    family: "spectral", // Spectral Beast (Corrupted)
    role: "sub_boss",
    ai: "choreographer",
    flavorLine: "She watched her cubs fall. She remembers every name.",
    // 2 · Anima Pool (spec-explicit — overrides role scaling) ------
    anima: 8000,
    phase2At: 3200, // 40%
    lyf: 12, // dial — sub-boss role law (40 + zone) walls out Beb
    //          attack entirely under flat subtraction; ⚑ roadmap
    // 3 · Arena ----------------------------------------------------
    arena: "Circular crypt. Four pillars. Shattered floor tiles at quarters.",
    // 4 · Telegraphed Attacks (wind-ups are spec; damage = dials ⚑) -
    attacks: {
      phantom_lunge: {
        id: "phantom_lunge",
        name: "Phantom Lunge",
        windupMs: 1500,
        damage: 60,
        counter: "dodge sideways",
        phase: [1, 2],
      },
      spectral_roar: {
        id: "spectral_roar",
        name: "Spectral Roar",
        windupMs: 2000,
        damage: 45,
        counter: "stand behind a pillar",
        phase: [1], // replaced in Phase 2
      },
      claw_sweep: {
        id: "claw_sweep",
        name: "Claw Sweep",
        windupMs: 1000,
        damage: 30,
        counter: "dodge through it",
        phase: [1, 2],
      },
      haunting_presence: {
        id: "haunting_presence",
        name: "Haunting Presence",
        windupMs: 2000,
        damage: 25,
        counter: "stand on the lit tile",
        phase: [2],
      },
    },
    // 5 · Mechanical Hook ------------------------------------------
    cubEveryMs: 30000,
    cubHealPct: 0.1, // each cub that reaches her heals 10% (800)
    // 6 · Phase Transition -----------------------------------------
    phaseTransition:
      "At 40% Anima the pillars shatter into rubble (walkable, no more cover). Spectral Roar is replaced by Haunting Presence.",
    // 7 · Enrage ----------------------------------------------------
    enrageMs: 6 * 60 * 1000,
    enrageAttackMult: 3, // triples, then triples again every 30s
    enrageStepMs: 30000,
    // 8 · Death Animation -------------------------------------------
    deathText:
      "She dissolves into motes of light. Each mote drifts to a pillar's grave, and a small monument forms.",
    // 9 · Drop Table -------------------------------------------------
    drops: {
      // Guaranteed Spectral-family Tome — Tomes are Batch 8; the
      // clear counter written on kill is the IOU Batch 8 derives from.
      tomeFamily: "spectral",
      cosmeticChance: 0.1,
      trophyChance: 0.02,
      trophyName: "The Matriarch's Whisker",
      // ⚑ Spec says "500 Divine Crests" — DC is READ-ONLY in this
      // app (IE lane law) and the roadmap's §4.2 follow-up re-specs
      // this app's DC pricing to the local currency (displayed as
      // "Blood Crests"; internal id stays bloodMoney). First-kill
      // bonus pays 500 here (a fat one-time reward that survives the
      // Jul 21 drop rescale intact — it's a landmark, not a dial).
      // Field name kept as firstClearBloodMoney so no reader breaks.
      firstClearBloodMoney: 500,
      clearBloodMoney: BLOOD_MONEY_DROP.sub_boss,
    },
  },
};

export const getBoss = (bossId) => BOSSES[bossId] || null;
